import React, { useState } from 'react';
import { Check, Loader2, Lock } from 'lucide-react';
import { ApiError, changePassword } from '../../api';

const inputCls =
  'bg-field text-fg placeholder:text-fg-subtle w-full rounded-lg border border-default px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-600';
const labelCls = 'mb-1 block text-xs font-semibold tracking-wide text-fg-secondary uppercase';

/** Card for changing the account password (shared by owners and customers). */
export default function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    setSaving(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSaved('Password updated');
      setTimeout(() => setSaved(''), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change password. Try again.');
    }
    setSaving(false);
  };

  return (
    <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
      <div className="border-subtle flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6 sm:py-5">
        <div>
          <h2 className="text-fg text-base font-bold sm:text-lg">Change Password</h2>
          <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
            Update the password you use to sign in
          </p>
        </div>
        {saved && (
          <span className="text-success flex shrink-0 items-center gap-1 text-xs font-medium sm:text-sm">
            <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            {saved}
          </span>
        )}
      </div>
      <form onSubmit={submit} className="space-y-3 px-4 py-4 sm:px-6">
        <div>
          <label htmlFor="cp-current" className={labelCls}>
            Current Password
          </label>
          <input
            id="cp-current"
            type="password"
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="cp-new" className={labelCls}>
            New Password
          </label>
          <input
            id="cp-new"
            type="password"
            required
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="cp-confirm" className={labelCls}>
            Confirm New Password
          </label>
          <input
            id="cp-confirm"
            type="password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputCls}
          />
        </div>
        {error && (
          <p role="alert" className="text-danger text-sm font-medium">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={saving}
          className="bg-brand-600 hover:bg-brand-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          Update Password
        </button>
      </form>
    </div>
  );
}
